import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { FKType } from './SQLBuilder'
import { User } from './User'

export type ContractConstructorArgs = ConstructorArgs<() => Contract>

export class Contract extends BaseRecord {
    readonly recordType = 'contract'

    name?: string
    memo?: string
    refnum?: string
    expires?: string
    edition?: string
    editionContract?: string
    associatedWithService?: boolean
    signature?: Image
    signatureData?: string
    alwaysRequire?: boolean
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<ContractConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = this.owner = PSApplication.me()
            return
        }

        this.name = data.name ?? undefined
        this.memo = data.memo ?? undefined
        this.refnum = data.refnum ?? undefined
        this.expires = data.expires ?? undefined
        this.associatedWithService = data.associated_with_service ?? false
        this.edition = data.edition ?? undefined
        this.editionContract = data.edition_contract ?? undefined
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.alwaysRequire = data.always_require ?? false
        this.signature = this.propertyFromImageArg(data.signature, data.signature_uri)
        this.signatureData = data.signature_data ?? undefined
    }

    clone(overrides?: Partial<ContractConstructorArgs>): Contract {
        return new Contract({ ...this.serializeForClone(), ...overrides })
    }

    expireString(): string {
        if (this.alwaysRequire) return 'always'
        if (this.expires) return this.expires
        return 'once'
    }

    deserialize(data: any): void {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.signature) this.signature = BaseRecord.fromSerializer(data.signature, Image)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
    }

    static dataType(): DataType {
        return 'contracts'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name ?? ''
        dict['memo'] = this.memo ?? ''
        dict['expires'] = this.expires ?? ''
        if (!!this.edition) {
            dict['edition'] = this.edition
        }
        if (!!this.editionContract) {
            dict['edition_contract'] = this.editionContract
        }
        if (!!this.signatureData) {
            dict['signature_data'] = this.signatureData
        }
        dict['always_require'] = this.alwaysRequire
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
